import { Plan } from '../types';

interface Props {
  form: Omit<Plan, 'id'>;
  set: (f: Partial<Omit<Plan, 'id'>>) => void;
}

const LimitRow = ({
  icon, label, hint, value, onChange, disabled,
}: {
  icon: string; label: string; hint?: string;
  value: number; onChange: (v: number) => void; disabled?: boolean;
}) => (
  <div className={`flex items-center justify-between py-3 border-b border-white/5 last:border-0 transition-opacity ${disabled ? 'opacity-30 pointer-events-none' : ''}`}>
    <div className="flex items-center gap-2">
      <i className={`${icon} text-white/30 text-[15px]`} />
      <div>
        <p className="text-[13px] text-white/70">{label}</p>
        {hint && <p className="text-[11px] text-white/30">{hint}</p>}
      </div>
    </div>
    <div className="flex items-center gap-2">
      <input
        type="number" min={-1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-24 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-[13px] text-right outline-none focus:border-violet-500"
      />
      <span className={`text-[11px] px-2 py-0.5 rounded-full ${value === -1 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-white/30'}`}>
        {value === -1 ? 'Ilimitado' : `máx ${value}`}
      </span>
    </div>
  </div>
);

const ToggleRow = ({
  icon, label, hint, value, onChange,
}: {
  icon: string; label: string; hint?: string;
  value: boolean; onChange: (v: boolean) => void;
}) => (
  <div className="flex items-center justify-between py-3 border-b border-white/5">
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${value ? 'bg-violet-600/20' : 'bg-white/5'}`}>
        <i className={`${icon} text-[13px] ${value ? 'text-violet-400' : 'text-white/30'}`} />
      </div>
      <div>
        <p className="text-[13px] text-white/70">{label}</p>
        {hint && <p className="text-[11px] text-white/30">{hint}</p>}
      </div>
    </div>
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0 overflow-hidden ${value ? 'bg-violet-600' : 'bg-white/10'}`}
    >
      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${value ? 'left-6' : 'left-1'}`} />
    </button>
  </div>
);

const PlanEditorLimits = ({ form, set }: Props) => (
  <div>
    <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3 mb-5 flex items-start gap-2">
      <i className="ri-information-line text-amber-400 mt-0.5" />
      <p className="text-[12px] text-amber-400/80">Use <strong>-1</strong> para ilimitado. Use <strong>0</strong> para bloquear acesso ao recurso.</p>
    </div>

    {/* Limites de uso */}
    <div className="bg-white/3 border border-white/8 rounded-2xl p-5 mb-5">
      <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-1">Limites de Uso</p>
      <LimitRow
        icon="ri-bank-line" label="Máx. contas bancárias"
        hint="Contas que o usuário pode cadastrar"
        value={form.accounts_limit} onChange={(v) => set({ accounts_limit: v })}
      />
      <LimitRow
        icon="ri-bank-card-line" label="Máx. cartões de crédito"
        hint="Cartões que o usuário pode cadastrar"
        value={form.cards_limit} onChange={(v) => set({ cards_limit: v })}
      />
      <LimitRow
        icon="ri-swap-line" label="Máx. lançamentos/mês"
        hint="Transações registradas por mês"
        value={form.monthly_transactions_limit} onChange={(v) => set({ monthly_transactions_limit: v })}
      />
      <LimitRow
        icon="ri-flag-line" label="Máx. metas simultâneas"
        hint="Metas financeiras ativas ao mesmo tempo"
        value={form.goals_limit} onChange={(v) => set({ goals_limit: v })}
      />
    </div>

    {/* Módulo de Favorecidos */}
    <div className="bg-white/3 border border-white/8 rounded-2xl p-5 mb-5">
      <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-1">Módulo de Favorecidos</p>
      <ToggleRow
        icon="ri-user-star-line"
        label="Acesso ao módulo de Favorecidos"
        hint="Habilita o cadastro e uso de favorecidos nos lançamentos"
        value={form.can_use_payees_module}
        onChange={(v) => set({ can_use_payees_module: v })}
      />
      <LimitRow
        icon="ri-team-line"
        label="Máx. favorecidos ativos"
        hint="Quantidade de favorecidos que podem ser cadastrados"
        value={form.payees_limit}
        onChange={(v) => set({ payees_limit: v })}
        disabled={!form.can_use_payees_module}
      />
    </div>

    {/* Visual summary */}
    <div className="grid grid-cols-3 lg:grid-cols-5 gap-3">
      {[
        { label: 'Contas', val: form.accounts_limit, icon: 'ri-bank-line', color: 'text-violet-400' },
        { label: 'Cartões', val: form.cards_limit, icon: 'ri-bank-card-line', color: 'text-cyan-400' },
        { label: 'Lanç./mês', val: form.monthly_transactions_limit, icon: 'ri-swap-line', color: 'text-emerald-400' },
        { label: 'Metas', val: form.goals_limit, icon: 'ri-flag-line', color: 'text-amber-400' },
        {
          label: 'Favorecidos',
          val: form.can_use_payees_module ? form.payees_limit : 0,
          icon: 'ri-user-star-line',
          color: form.can_use_payees_module ? 'text-rose-400' : 'text-white/20',
          blocked: !form.can_use_payees_module,
        },
      ].map((item) => (
        <div key={item.label} className={`bg-white/3 border border-white/8 rounded-xl p-3 text-center transition-opacity ${'blocked' in item && item.blocked ? 'opacity-40' : ''}`}>
          <i className={`${item.icon} text-[20px] ${item.color} mb-1`} />
          <p className="text-[18px] font-700 text-white">
            {'blocked' in item && item.blocked ? <i className="ri-lock-line text-[16px]" /> : (item.val === -1 ? '∞' : item.val)}
          </p>
          <p className="text-[10px] text-white/40">{item.label}</p>
        </div>
      ))}
    </div>
  </div>
);

export default PlanEditorLimits;
